import { getTimePanelSessionId } from './selectors/telemetrySelectors';
import { getMailAriaTenantTokens } from 'owa-analytics';
import { OwaWorkload } from 'owa-workloads';
import { TimePanelSource } from './store/schema/TimePanelStore';
import { CustomData, DatapointConfig } from 'owa-analytics-actions';
import { differenceInMilliseconds, now } from 'owa-datetime';
import getStore from './store/store';

/** List of Time Panel datapoints */
export interface TimePanelDatapoints {
    [name: string]: DatapointConfig;
}

/** Custom data properties included on timePanelActionOpen datapoint */
export interface TimePanelOpenCustomData {
    /** Bitmask identifier for the module/workload in which the panel session was started  */
    workload: OwaWorkload;
    /** Friendly name for the module/workload in which the panel session was started  */
    workloadName: string;
    /** Source for starting the panel session */
    source: TimePanelSource;
}

/** Possible targets deep-linked from Time Panel */
export type TimePanelTarget = 'CalendarModule' | 'TasksModule';

export const getTimePanelOptions = {
    isCore: true,
    tenantTokens: getMailAriaTenantTokens(),
};

/**
 * Time Panel datapoints are unsampled, so all datapoints are logged for 100% of users.
 *
 * The data is stored in the shared OWA tenant.
 *
 * A Time Panel session is the period of time between a panel open and panel close. Each time the panel opens
 * this is considered a new session, and a Time Panel session ID is created. This ID is logged in the custom
 * data of each datapoint, so each datapoint can be associated with a particular panel session.
 *
 * There are two types of Time Panel datapoints:
 * 1. Panel action datapoints (prefixed with ```timePanelAction```): actions that the user takes on the panel
 * level, i.e switch tabs, open panel, close panel, etc.
 * 2. Panel performance datapoints (prefixed with ```timePanelPerformance```): performance measurements associated
 * with various panel actions, i.e. how long to switch between modules/pivots
 */
export const Datapoints: TimePanelDatapoints = {
    // 1. Panel action datapoints //

    /**
     * Logged when the panel is closed/dismissed (via "X" or light dismiss)
     */
    timePanelActionClose: {
        name: 'timePanelActionClose',
        options: getTimePanelOptions,
        customData: getTimePanelCustomData({
            sessionDuration: getSessionDuration(),
        }),
    },

    // 2. Panel performance datapoints //

    // FUTURE: add performance datapoints, e.g. loading panel, switching between Calendar and Tasks pivots
};

function getSessionDuration(): number {
    const panelOpenTimestamp = getStore().panelOpenTimestamp;
    return panelOpenTimestamp ? differenceInMilliseconds(now(), panelOpenTimestamp) : null;
}

/**
 * Generates custom data for Time Panel datapoints by mixing datapoint-specific custom data with default custom data
 */
export function getTimePanelCustomData(datapointSpecificCustomData?: CustomData): CustomData {
    return {
        ...datapointSpecificCustomData,
        session: getTimePanelSessionId(),
    };
}
